import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Activity, ChevronDown, ChevronUp, ShieldOff, Users, AlertTriangle } from 'lucide-react'
import { partnerApiService } from '@/services/partnerApiService'
import LoadingSpinner from '../../components/ui/LoadingSpinner'

/* 산하 회원 실시간 현황 (2026-08-31 사장님 — 파트너 요청 / 9-01 관리자 실시간 탭과 디자인 통일)
 * - 권한(can_view_realtime) 없으면 403 → 안내 화면 (사이드바 메뉴 자체도 숨김이지만 URL 직접접근 방어)
 * - 5초 폴링, 회원 카드 클릭 → 전 엔진(NF/코인/해외) 포지션 상세 펼침 (관리자와 동일 컬럼)
 * - 손익 색: 이익=빨강/손실=파랑, 롱=빨강/숏=파랑 (국내 HTS 관례 — 회원 화면과 동일) */

const fmtKrw = (v: any) => {
  const n = Math.round(Number(v || 0))
  return `${n > 0 ? '+' : ''}${n.toLocaleString('ko-KR')}원`
}
const fmtKrwPlain = (v: any) => `${Math.round(Number(v || 0)).toLocaleString('ko-KR')}원`
const fmtUsdt = (v: any) => Number(v || 0).toLocaleString('ko-KR', { maximumFractionDigits: 2 })
const pnlColor = (v: any) => {
  const n = Number(v || 0)
  return n > 0 ? 'text-red-400' : n < 0 ? 'text-blue-400' : 'text-dark-300'
}
const fmtNum = (v: any) => {
  if (v === null || v === undefined || v === '') return '-'
  const s = String(v)
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s
}
const fmtOpenedAt = (iso: any) => {
  if (!iso) return '-'
  const s = String(iso)
  return `${s.slice(5, 10)} ${s.slice(11, 19)}`
}
const ROOM_WARN = 500000  // 로스컷 여유 50만원 미만 = 경고 (관리자와 동일 기준)
const roomWarn = (v: any) => v != null && v !== '' && Number(v) < ROOM_WARN

// 통화 라벨 칩 — 관리자 실시간 탭과 동일 (원화=하늘 / USDT=녹색)
const WalletChips = ({ krw, usdt }: { krw: any; usdt: any }) => {
  const hasKrw = Number(krw || 0) !== 0
  const hasUsdt = Number(usdt || 0) !== 0
  if (!hasKrw && !hasUsdt) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-px rounded whitespace-nowrap bg-sky-500/15 tabular-nums">
        <b className="text-sky-300 font-semibold">원화</b><span className="text-dark-300">0원</span>
      </span>
    )
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {hasKrw && (
        <span className="inline-flex items-center gap-1 px-1.5 py-px rounded whitespace-nowrap bg-sky-500/15 tabular-nums">
          <b className="text-sky-300 font-semibold">원화</b>
          <span className="text-dark-100">{fmtKrwPlain(krw)}</span>
        </span>
      )}
      {hasUsdt && (
        <span className="inline-flex items-center gap-1 px-1.5 py-px rounded whitespace-nowrap bg-emerald-500/15 tabular-nums">
          <b className="text-emerald-300 font-semibold">USDT</b>
          <span className="text-dark-100">{fmtUsdt(usdt)}</span>
        </span>
      )}
    </span>
  )
}

const PartnerRealtime = () => {
  const { t } = useTranslation('realtime')
  const [data, setData] = useState<any | null>(null)
  const [denied, setDenied] = useState(false)
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [updatedAt, setUpdatedAt] = useState('')
  const [live, setLive] = useState(false)
  const timerRef = useRef<number | null>(null)

  const ENGINE_BADGE: Record<string, { label: string; cls: string }> = {
    NF: { label: t('engineNf'), cls: 'bg-emerald-500/20 text-emerald-300' },
    COIN: { label: t('engineCoin'), cls: 'bg-yellow-500/20 text-yellow-300' },
    OFUT: { label: t('engineOfut'), cls: 'bg-purple-500/20 text-purple-300' },
  }

  const load = async () => {
    try {
      const res = await partnerApiService.getRealtime()
      const d = (res && res.data) || res
      if (d && d.totals) {
        setData(d)
        setUpdatedAt(new Date().toLocaleTimeString('ko-KR', { hour12: false }))
        setDenied(false)
        setLive(true)
      }
    } catch (e: any) {
      if (e?.response?.status === 403) {
        setDenied(true)
        if (timerRef.current) window.clearInterval(timerRef.current)
      }
      else setLive(false)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    timerRef.current = window.setInterval(load, 5000)
    return () => { if (timerRef.current) window.clearInterval(timerRef.current) }
  }, [])

  if (loading) {
    return <div className="p-6 flex items-center justify-center min-h-96"><LoadingSpinner /></div>
  }

  if (denied) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <div className="text-center">
          <ShieldOff className="w-10 h-10 text-dark-400 mx-auto mb-3" />
          <h3 className="text-xl font-semibold text-white mb-2">{t('title')}</h3>
          <p className="text-dark-400">{t('noPermission')}</p>
        </div>
      </div>
    )
  }

  const totals = data?.totals || {}
  const members: any[] = data?.members || []
  const warnCount = members.filter(m => roomWarn(m.losscut_room_krw)).length
  const todayTotal = Number(totals.daily_realized_total_krw || 0) + Number(totals.unrealized_total_krw || 0)

  const engineCounts = (m: any) => {
    const c = m.position_counts || {}
    const parts: string[] = []
    if (c.nf) parts.push(`${t('engineNf')} ${c.nf}`)
    if (c.coin) parts.push(`${t('engineCoin')} ${c.coin}`)
    if (c.ofut) parts.push(`${t('engineOfut')} ${c.ofut}`)
    return parts.length ? parts.join(' · ') : t('noPosition')
  }

  const unrealLabel = (m: any) => {
    const krw = Number(m.unrealized_pnl_krw || 0)
    if (!krw) return '0원'
    return `${fmtKrw(krw)} (${Number(m.unrealized_pnl_usdt || 0) > 0 ? '+' : ''}${fmtUsdt(m.unrealized_pnl_usdt)}$)`
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* 헤더 */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2">
            <Activity className="w-6 h-6 text-emerald-400" /> {t('title')}
          </h1>
          <p className="text-dark-400 text-sm mt-1">{t('subtitle')}</p>
        </div>
        <span className="text-xs text-dark-400 flex items-center gap-1.5">
          <Activity className={`w-3.5 h-3.5 ${live ? 'text-emerald-400' : 'text-dark-500'}`} />
          {t('updated')} {updatedAt}
        </span>
      </div>

      {/* 합계 바 — 관리자 실시간 탭과 동일 구성 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="bg-dark-800/60 border border-dark-700 px-3 py-2 rounded-xl flex items-center gap-2">
          <Users className="w-4 h-4 text-dark-400 shrink-0" />
          <div>
            <p className="text-[10px] text-dark-400">{t('activeMembers')} / {t('totalMembers')}</p>
            <p className="text-sm font-bold text-white tabular-nums">{totals.active_count ?? 0}명 <span className="text-dark-400 font-normal">/ {totals.member_count ?? 0}명</span></p>
          </div>
        </div>
        <div className="bg-dark-800/60 border border-dark-700 px-3 py-2 rounded-xl">
          <p className="text-[10px] text-dark-400">{t('todayTotal')}</p>
          <p className={`text-sm font-bold tabular-nums ${pnlColor(todayTotal)}`}>{fmtKrw(todayTotal)}</p>
          <p className={`text-[10px] tabular-nums ${pnlColor(totals.daily_realized_total_krw)}`}>{t('realizedShort')} {fmtKrw(totals.daily_realized_total_krw)}</p>
        </div>
        <div className="bg-dark-800/60 border border-dark-700 px-3 py-2 rounded-xl">
          <p className="text-[10px] text-dark-400">{t('unrealized')}</p>
          <p className={`text-sm font-bold tabular-nums ${pnlColor(totals.unrealized_total_krw)}`}>{fmtKrw(totals.unrealized_total_krw)}</p>
          <p className="text-[10px] text-dark-500 tabular-nums">{t('positionHolders')} {totals.position_holders ?? 0}명</p>
        </div>
        <div className={`bg-dark-800/60 px-3 py-2 rounded-xl border ${warnCount ? 'border-red-500/40' : 'border-dark-700'}`}>
          <p className="text-[10px] text-dark-400">{t('losscutWarn')} (여유 50만↓)</p>
          <p className={`text-sm font-bold tabular-nums ${warnCount ? 'text-red-400' : 'text-white'}`}>
            {warnCount ? `⚠️ ${warnCount}명` : t('none')}
          </p>
        </div>
      </div>

      {/* 회원 카드 리스트 — 관리자 좌측 리스트와 동일 정보 구성 */}
      {members.length === 0 ? (
        <div className="bg-dark-800/60 border border-dark-700 rounded-xl p-10 text-center text-dark-400">{t('noActive')}</div>
      ) : (
        <div className="space-y-1.5">
          {members.map((m) => {
            const open = expanded === m.user_id
            const poss: any[] = m.positions || []
            return (
              <div key={m.user_id} className={`bg-dark-800/60 rounded-xl border transition-colors ${
                open ? 'border-emerald-500/40' : 'border-dark-700 hover:border-dark-500'}`}>
                <button
                  className="w-full p-3 text-left"
                  onClick={() => setExpanded(open ? null : m.user_id)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-white text-sm font-medium truncate flex items-center gap-1.5 min-w-0">
                      {open ? <ChevronUp className="w-4 h-4 text-dark-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-dark-400 shrink-0" />}
                      <span className="truncate">
                        {m.name ? `${m.name}${m.nickname && m.nickname !== m.name ? `(${m.nickname})` : ''}` : (m.nickname || `#${m.user_id}`)}
                      </span>
                    </p>
                    <span className={`text-sm font-bold tabular-nums shrink-0 ${pnlColor(m.today_pnl_krw)}`}>
                      오늘 {fmtKrw(m.today_pnl_krw)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1 text-[11px] pl-[22px]">
                    <span className="text-dark-400 shrink-0">{engineCounts(m)}</span>
                    <span className={`tabular-nums truncate ${pnlColor(m.unrealized_pnl_krw)}`}>
                      {t('unrealized').replace(' 합계', '')} {unrealLabel(m)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] pl-[22px]">
                    <span className="flex items-center gap-1">
                      <span className="text-dark-500 shrink-0 whitespace-nowrap">{t('balance')}</span>
                      <WalletChips krw={m.balance_krw} usdt={m.balance_usdt} />
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-dark-500 shrink-0 whitespace-nowrap">{t('bonus')}</span>
                      <WalletChips krw={m.bonus_krw} usdt={m.bonus_usdt} />
                    </span>
                    <span className={`ml-auto shrink-0 flex items-center gap-1 ${roomWarn(m.losscut_room_krw) ? 'text-red-400 font-bold' : 'text-dark-400'}`}>
                      {roomWarn(m.losscut_room_krw) && <AlertTriangle className="w-3 h-3" />}
                      {t('losscutRoom')} {m.losscut_room_krw != null ? fmtKrwPlain(m.losscut_room_krw) : '-'}
                    </span>
                  </div>
                </button>
                {open && (
                  <div className="px-3 pb-3">
                    {poss.length === 0 ? (
                      <p className="text-sm text-dark-400 py-3 text-center">{t('noPosition')}</p>
                    ) : (
                      <div className="overflow-x-auto rounded-lg bg-dark-900/50 px-2">
                        <table className="w-full text-sm whitespace-nowrap">
                          <thead>
                            <tr className="text-xs text-dark-400 border-b border-white/10">
                              <th className="text-left py-2 px-2 font-normal">{t('type')}</th>
                              <th className="text-left py-2 px-2 font-normal">{t('member')}</th>
                              <th className="text-center py-2 px-2 font-normal">{t('side')}</th>
                              <th className="text-right py-2 px-2 font-normal">{t('qty')}</th>
                              <th className="text-right py-2 px-2 font-normal">{t('avgEntry')}</th>
                              <th className="text-right py-2 px-2 font-normal">{t('current')}</th>
                              <th className="text-right py-2 px-2 font-normal">{t('liq')}</th>
                              <th className="text-right py-2 px-2 font-normal">{t('pnl')}</th>
                              <th className="text-right py-2 px-2 font-normal">{t('openedAt')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {poss.map((p, i) => {
                              const badge = ENGINE_BADGE[p.engine] || { label: p.engine, cls: 'bg-dark-600 text-dark-300' }
                              const pnl = p.unrealized_pnl_krw ?? p.unrealized_pnl_usdt
                              const isKrw = p.unrealized_pnl_krw != null
                              const isLong = String(p.side).includes('LONG')
                              return (
                                <tr key={i} className="border-b border-white/5 last:border-0">
                                  <td className="py-2 px-2">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${badge.cls}`}>{badge.label}</span>
                                  </td>
                                  <td className="py-2 px-2 text-white">{p.display_name || p.symbol}</td>
                                  <td className="py-2 px-2 text-center">
                                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                                      isLong ? 'bg-red-500/15 text-red-400' : 'bg-blue-500/15 text-blue-400'}`}>
                                      {isLong ? t('long') : t('short')}
                                    </span>
                                  </td>
                                  <td className="py-2 px-2 text-right text-dark-200 tabular-nums">{fmtNum(p.qty)}</td>
                                  <td className="py-2 px-2 text-right text-dark-200 tabular-nums">{fmtNum(p.avg_entry_price)}</td>
                                  <td className="py-2 px-2 text-right text-dark-200 tabular-nums">{fmtNum(p.current_price)}</td>
                                  <td className="py-2 px-2 text-right text-dark-200 tabular-nums">{fmtNum(p.liquidation_price)}</td>
                                  <td className={`py-2 px-2 text-right font-bold tabular-nums ${pnlColor(pnl)}`}>
                                    {pnl != null ? (isKrw ? fmtKrw(pnl) : `${Number(pnl) > 0 ? '+' : ''}${Number(pnl).toFixed(2)} USDT`) : '-'}
                                  </td>
                                  <td className="py-2 px-2 text-right text-dark-300 tabular-nums text-xs">{fmtOpenedAt(p.opened_at)}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {members.length > 0 && (
        <p className="text-[11px] text-dark-500">{t('note')}</p>
      )}
    </div>
  )
}

export default PartnerRealtime
